import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center text-[10px] font-bold text-white rounded-full border px-4 py-1 md:py-2 w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-1 text-sm border-gray-50/10 bg-gradient-to-r from-zinc-50/10 to-secondary/20 [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-gradient-to-r from-zinc-100/20 via-zinc-50/50 to-zinc-100/20",
        destructive:
          "border-transparent bg-destructive [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        "metal-gray-gradient":
          "bg-gradient-to-tr from-[#4b4b4b] via-[#737373] to-[#4b4b4b] border-[#656565] text-white",
        "glass-white":
          "bg-white/10 text-white backdrop-blur-sm border border-white/20",
        "plain-gray": "'bg-[#ffffff]/4 text-white border-[#ffffff]/10",
        "light-to-dark-gradient":
          "bg-brand-gradient border-transparent text-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
